<script>
import moment from 'moment'
import { mapState } from 'vuex'

import { FILTER_EVENT, RESET_FILTER_EVENT, FilterBus } from '@/components/list/filters/Filter.vue'
import { SORT_EVENT, SortBus } from '@/components/list/Sorter.vue'

import { useCriteriaToParam } from '@/helpers/criteria'
import { actions as clientActions } from '@/store/clients'
import { actions as siteActions } from '@/store/sites'

const INITIAL_STATE = {
    dates: { from: null, to: null },
    defaultFilters: {},
    filters: [],
    filterTimer: null,
    isFiltering: false,
    isLoadingMore: false,
    isReady: false,
    page: 1,
    pageOrderBy: null,
    pageSortBy: null,
    searchCount: 0,
    searchResult: [],
    service: null,
    store: null
}

const DATE_PATTERN = 'YYYY-MM-DD'
const FILTER_DELAY = 300
const STORAGE_KEY = 'filters'

export default {

    data: () => ({
        ...INITIAL_STATE
    }),

    computed: {

        api: function() {
            return ( this.isFiltered || !this.store ) ? this.service : this.store;
        },

        appliedFilters: function() {
            let filters = {}
            for ( const filter in this.filters) {
                if (
                    typeof this.defaultFilters[filter] !== 'undefined' &&
                    this.defaultFilters[filter] !== this.filters[filter]
                ) {
                    filters[filter] = this.filters[filter]
                }
            }
            return filters
        },
        
        isFiltered: function() {
            return this.filterCount > 0
        },

        filterCount: function() {
            return Object.keys(this.appliedFilters).length
        },

        hasContent: function() {
            return ( this.count > 0 )
        },

        hasMore: function() {
            return ( this.items.length < this.count)
        },

        params: function() {

            let params = {}
            for ( const filter in this.filters ) {

                const key = filter
                let value = this.filters[filter] 

                if ( value === null ) {
                    continue
                }

                if ( typeof value === 'string' || value instanceof String ) {
                    if ( value === '' ) {
                        continue
                    }
                }

                params[key] = value
            }

            if ( this.pageOrderBy ) {
                params = { ...params, ...{ order_by: useCriteriaToParam(this.pageOrderBy) } }
            }

            if ( this.pageSortBy ) {
                params = { ...params, ...{ sort_by: useCriteriaToParam(this.pageSortBy) } }
            }

            return params
        },

        savedFilters: function() {
            if ( !localStorage.getItem(STORAGE_KEY) ) localStorage.setItem(STORAGE_KEY, JSON.stringify({}))
            return JSON.parse(localStorage.getItem(STORAGE_KEY))[this.$router.currentRoute.path] ?? {}
        },
        
        ...mapState({

            count: function(state) {
                return ( this.isFiltered || !this.store ) ? this.searchCount : state[this.store].count
            },

            items: function(state) {
                return ( this.isFiltered || !this.store ) ? this.searchResult : [...( state[this.store].all || [] )]
            }
        })

    },

    methods: {

        applyDefaultFilters: function(filters) {
            Object.assign( this.defaultFilters, filters )
        },

        handleChange: function(newValue) {

            this.isFiltering = true

            if ( this.filters.query == newValue ) {
                if ( this.filterTimer ) {
                    clearTimeout(this.filterTimer)
                }
                this.filterTimer = setTimeout(() => this.filter(), FILTER_DELAY)
            }
            else {
                this.filter()
            }
        },

        init: async function() {

            if ( !this.service ) {
                console.warn("⚠️ La méthode useFilter() doit être appelée avant d'utiliser la méthode search().")
                return;
            }

            try {
                this.isFiltering = true
                
                if (
                    typeof this.filters.client_id !== 'undefined' ||
                    typeof this.filters.site_id !== 'undefined' 
                ) {                        
                    let values = {}
                    const [ currentClient, currentClientAddress ] = await Promise.all([
                        this.$store.dispatch(clientActions.GET),
                        this.$store.dispatch(siteActions.GET)
                    ])

                    if (
                        typeof this.defaultFilters.client_id !== 'undefined' &&
                        this.filters.client_id === null
                    ) {
                        this.applyDefaultFilters({
                            client_id: currentClient.id,
                        })

                        values.client_id = ( typeof this.savedFilters.client_id !== 'undefined' ) ?
                            this.savedFilters.client_id :
                            currentClient.id
                    }

                    if (
                        typeof this.defaultFilters.site_id !== 'undefined' &&
                        this.filters.site_id === null
                    ) {
                        this.applyDefaultFilters({
                            site_id: currentClientAddress.site.id
                        })

                        values.site_id = ( typeof this.savedFilters.site_id !== 'undefined' ) ?
                            this.savedFilters.site_id :
                            currentClientAddress.site.id
                    }
                    
                    this.filters = {
                        ...this.filters,
                        ...values,       
                    } 
                }

                const [ count, items ] = ( this.store ) ?
                    await this.$store.dispatch(this.store + '/getAll') :
                    await Promise.all([
                        this.service.count(this.params),
                        this.service.list(this.params)
                    ])
                    
                this.page = 1
                this.searchCount = count
                this.searchResult = items
            }
            catch( error ) {
                console.error(error)
                console.error("💥 Impossible d'initialiser les données.")
                this.$errorPage(error.reason)
            }
            finally {
                this.isFiltering = false
                this.isReady = true
            }
        },

        filter: function() {
            this.isFiltering = true
            this.filterTimer = null

            if ( !localStorage.getItem(STORAGE_KEY) ) localStorage.setItem(STORAGE_KEY, JSON.stringify({}))
            const savedFilter = JSON.parse( localStorage.getItem(STORAGE_KEY) )

            if ( this.isFiltered ) {
                this.search()
                let filters = {}
                for ( const filter in this.filters) {
                    if (
                        typeof this.defaultFilters[filter] !== 'undefined' &&
                        this.defaultFilters[filter] !== this.filters[filter]
                    ) {
                        filters[filter] = this.filters[filter]
                    }
                }
                savedFilter[this.$router.currentRoute.path] = filters
            }
            else {
                this.init()
                delete savedFilter[this.$router.currentRoute.path]
            }
            
            localStorage.setItem(STORAGE_KEY, JSON.stringify(savedFilter))
        },

        next: function() {

            if ( !this.service ) {
                console.warn("⚠️ La méthode useFilter() doit être appelée avant d'utiliser la méthode next().")
                return;
            }

            this.isLoadingMore = true
            
            if ( this.isFiltered || !this.store ) {
                let params =  { ...this.params, ...{ page: this.page + 1 } };
                this.service.search(params)
                    .then(results => {
                        this.page++
                        this.searchResult = [ ...this.searchResult, ...results ]
                    })
                    .catch(error => {
                        this.$error(error.reason)
                    })
                    .finally( () => this.isLoadingMore = false )
            }
            else {
                this.$store.dispatch(this.store + '/getNext')
                    .catch(error => {
                        this.$error(error.reason)
                    })
                    .finally( () => this.isLoadingMore = false )
            }
        },

        reset: function() {
            Object.assign( this.filters, this.defaultFilters )
            this.dates = { from: null, to: null }
            this.pageOrderBy = null
            this.pageSortBy = null
            window.scrollTo({ top: 0, behavior: 'smooth' })
        },

        search: function() {

            if ( !this.service ) {
                console.warn("⚠️ La méthode useFilter() doit être appelée avant d'utiliser la méthode search().")
                return;
            }

            this.isFiltering = true

            Promise.all([
                    this.service.count(this.params),
                    this.service.search(this.params)
                ])
                .then( ([ count, items ]) => {
                    this.page = 1
                    this.searchCount = count
                    this.searchResult = items
                })
                .catch(error => {
                    this.$error(error.reason)
                })
                .finally( () => this.isFiltering = false )
        },

        useFilter: function(service, store) {
            this.service = service
            this.store = store
            this.defaultFilters = { ...INITIAL_STATE.defaultFilters }
            
            this.applyDefaultFilters(this.filters)
            this.init()
        }
    },

    created() {

        for ( const filter in this.filters) {
            this.$watch(() => this.filters[filter], this.handleChange);
        }

        FilterBus.$on(FILTER_EVENT, filter => {

            if ( filter.criteria === 'client_id' ) {
                this.filters.site_id = null
            }
        })

        FilterBus.$on(RESET_FILTER_EVENT, () => this.reset())
        
        SortBus.$on(SORT_EVENT, sort => {
            this.pageOrderBy = sort.orderBy
            this.pageSortBy = sort.sortBy
        })
    },

    watch: {

        dates: function(newDates) {
            
            if ( typeof this.filters.from !== 'undefined' ) {
                this.filters.from = ( newDates.from ) ? moment(newDates.from).format(DATE_PATTERN) : null
            }

            if ( typeof this.filters.to !== 'undefined' ) {
                this.filters.to = ( newDates.to ) ? moment(newDates.to).format(DATE_PATTERN) : null
            }
        },

        'pageOrderBy': function() {
            this.filter()
        },

        'pageSortBy': function() {            
            this.filter()
        }
    },
}

</script>
 